import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'

const AttachmentContext = createContext(null)

export function AttachmentProvider ({ children, threadId, focus }) {
  const [selectedAttachment, setSelectedAttachment] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const previousThreadIdRef = useRef(threadId)

  // Close attachment when thread changes
  useEffect(() => {
    if (threadId !== previousThreadIdRef.current && isOpen) {
      setIsOpen(false)
      setSelectedAttachment(null)
    }
    previousThreadIdRef.current = threadId
  }, [threadId, isOpen])

  // Open an attachment
  const openAttachment = useCallback((attachment) => {
    setSelectedAttachment(attachment)
    setIsOpen(true)
  }, [])

  // Close the attachment viewer
  const closeAttachment = useCallback(() => {
    setIsOpen(false)
    // Delay clearing attachment for exit animation
    setTimeout(() => setSelectedAttachment(null), 300)
  }, [])

  const value = useMemo(() => ({
    selectedAttachment,
    isOpen,
    openAttachment,
    closeAttachment,
    focus
  }), [selectedAttachment, isOpen, openAttachment, closeAttachment, focus])

  return (
    <AttachmentContext.Provider value={value}>
      {children}
    </AttachmentContext.Provider>
  )
}

AttachmentProvider.propTypes = {
  children: PropTypes.node,
  threadId: PropTypes.string,
  focus: PropTypes.object
}

export function useAttachment () {
  const context = useContext(AttachmentContext)
  if (!context) {
    throw new Error('useAttachment must be used within an AttachmentProvider')
  }
  return context
}
